import { compressToBase64 } from 'lz-string';
import React, { FC, useLayoutEffect, useRef, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import { useNavigate } from 'react-router-dom';
import { Section } from 'src/components/widgets/section';
import UpgradeDialog from 'src/components/widgets/upgradeDialog/upgradeDialog';
import { useRelayStore } from 'src/relay';
import config from 'src/shared/config';
import { ProductStatus } from 'src/shared/enums';
import { useFeatureFlag } from 'src/shared/feature-flags';
import { backofficeUrl, stringFormat, T } from 'src/shared/helpers';
import usePermissions from 'src/shared/hooks/usePermissions';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import NoImage from 'src/shared/svg/noImage';
import styled from 'styled-components';
import { BasicButton, PrimaryButton } from '../../../../widgets/buttons';
import { useDialogManager } from '../../../../widgets/dialog';
import { PageFooterButtons } from '../../../../widgets/headers';
import { ContainerBase } from '../../../../widgets/layouts';
import { removeNotification, showErrorNotification, showNotification } from '../../../../widgets/notificationToaster';
import {
    loadProductAction,
    setAreasWereDeletedAction,
    toUpdateMutationInput,
    useProductConfigurationChangeStep,
    useProductConfigurationState
} from '../../state';
import { isDefaultColor } from '../../state/model/modelColor';
import { WizardFooterBackButton } from '../wizardFooterBackButton';
import { previewQuery } from './__generated__/previewQuery.graphql';
import {
    ModelColorMappingInput,
    previewSetModelColorMappingSalesChannelsMutation,
    previewSetModelColorMappingSalesChannelsMutation$data
} from './__generated__/previewSetModelColorMappingSalesChannelsMutation.graphql';
import { previewUpdateMutation, previewUpdateMutation$data } from './__generated__/previewUpdateMutation.graphql';

const ProductImageContainer = styled.div`
    width: 100%;
`;

const ProductImage = styled.img`
    width: 100%;
    object-fit: contain;
    border: 1px #ddd solid;
    place-items: center;
    padding: 20px;
    border-radius: 8px;
`;

const PreviewCardContentWrapper = styled.div`
    display: grid;
    grid-template-columns: 300px 1fr;
    column-gap: 50px;
    max-width: 1000px;
    /* Center the content within the review card. */
    margin: 0 auto;
`;

// From-scratch review card: same width/height as the attributes-management section (fills down to just
// above the fixed footer, no wasted gap).
const ReviewSection = styled(Section)`
    height: calc(100vh - 150px);
    max-width: 1400px;
    margin-left: auto;
    margin-right: auto;
    /* The card itself must never scroll — only the Product Structure list inside it does. */
    overflow: hidden;
`;

const SaveTitle = styled.h2`
    text-align: center;
    font-size: 14px;
    font-weight: 400;
    color: #546e7a;
    padding: 0 0 24px;
`;

const ProductDetailsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const ProductInfoRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #374151;

    span {
        color: #9ca3af;
    }
`;

const DetailsSectionTitle = styled.div`
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #9ca3af;
`;

const DetailsTree = styled.div<{ $fill?: boolean }>`
    margin-top: 8px;
    border: 1px solid #f3f4f6;
    border-radius: 8px;
    overflow: hidden;
    /* On the from-scratch full-height card, use the available section space before scrolling
       (scroll only kicks in when the list is taller than the card). */
    max-height: ${(p) => (p.$fill ? 'calc(100vh - 280px)' : '300px')};
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 4px;
    }
`;

const TreeVariant = styled.div`
    &:not(:last-child) {
        border-bottom: 1px solid #f3f4f6;
    }
`;

const TreeVariantHeader = styled.div<{ $clickable?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    background: #fafbfc;
    cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
    user-select: none;

    &:hover {
        background: ${(p) => (p.$clickable ? '#f3f4f6' : '#fafbfc')};
    }
`;

const TreeSide = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px 6px 28px;
    font-size: 12px;
    color: #6b7280;
    border-top: 1px solid #f9fafb;
`;

const TreePrintArea = styled.div`
    display: flex;
    align-items: baseline;
    gap: 6px;
    padding: 4px 12px 4px 48px;
    font-size: 11px;
    color: #9ca3af;
    border-top: 1px solid #fafbfc;
`;

const TreeIcon = styled.span`
    font-size: 8px;
    flex-shrink: 0;
    width: 12px;
    text-align: center;
`;

const TreePrintAreaDim = styled.span`
    font-size: 10px;
    color: #b0b7c3;
    margin-left: 2px;
    &::before {
        content: '·';
        margin-right: 4px;
    }
`;

const TreeItemMeta = styled.span`
    font-size: 12px;
    color: #9ca3af;
`;

const Preview: FC<{}> = (props) => {
    const invalidate = useRelayStore((state) => state.invalidateRelayStore);
    const { state, dispatch, unsavedChanges } = useProductConfigurationState();
    const changeStep = useProductConfigurationChangeStep();
    const { isAllowed } = usePermissions();
    const { showDialog } = useDialogManager();
    const navigate = useNavigate();
    const { sellerMarketplace } = useSubscriptionData();
    const isStandaloneConversionEnabled = useFeatureFlag('standaloneProductConversion');

    // Queries
    const query = useLazyLoadQuery<previewQuery>(
        graphql`
            query previewQuery {
                subscription {
                    maxProducts {
                        pricingVersion
                        numProducts
                        numPublishedProducts
                        maxProducts
                    }
                }
                salesChannels(status: ACTIVE) {
                    ecommerceSettings {
                        ecommerceName
                    }
                }
            }
        `,
        {}
    );

    const [commitUpdate, isSavingProduct] = useMutation<previewUpdateMutation>(graphql`
        mutation previewUpdateMutation($input: ModelUpdateOrCreateInput, $compressedInput: String) {
            createOrUpdateModelV2(input: $input, compressedInput: $compressedInput) {
                model {
                    ...productFragment_model @relay(mask: false)
                }
            }
        }
    `);

    const [updateModelColorMappingsSalesChannels, isUpdateMappingSalesChannelsInProgress] = useMutation<previewSetModelColorMappingSalesChannelsMutation>(
        graphql`
            mutation previewSetModelColorMappingSalesChannelsMutation($modelID: ID!, $modelColorMappings: [ModelColorMappingInput!]!) {
                setModelColorMapping(modelID: $modelID, modelColorMappings: $modelColorMappings) {
                    colorID
                    merchantCode
                }
            }
        `
    );

    const [saving, setSaving] = useState(false);

    const subscription = query.subscription;
    const isStandaloneProduct = state.ecommerceSalesChannel.length === 0 || state.ecommerceSalesChannel[0] === 'NONE';
    const hasOnlineStore = query.salesChannels.some((channel) => (channel.ecommerceSettings?.ecommerceName ?? 'NONE') !== 'NONE');

    const handleSaveClick = async (productStatus?: ProductStatus, destination?: 'STORE' | 'CONVERT') => {
        if (!saving) {
            performSave(productStatus, false, destination);
        }
    };

    const performSave = async (productStatus?: ProductStatus, skipPlanLimit = false, destination?: 'STORE' | 'CONVERT') => {
        setSaving(true);
        const pricingVersion = subscription?.maxProducts.pricingVersion ?? 'VERSION_1';
        const numCurrentPublishedProducts = subscription?.maxProducts.numPublishedProducts ?? 0;
        const numMaxPublishedProducts = subscription?.maxProducts.maxProducts ?? 0;
        const exceedingMaxProductsPublished = numCurrentPublishedProducts >= numMaxPublishedProducts;
        if (pricingVersion === 'VERSION_2' && productStatus) {
            if (!isAllowed(['PRODUCTS_PUBLISH'])) {
                showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
                setSaving(false);
                return;
            } else if (productStatus === ProductStatus.Published && exceedingMaxProductsPublished && !skipPlanLimit) {
                showDialog(
                    'Upgrade_Plan',
                    <UpgradeDialog
                        redirectUrlFromDashboard={encodeURI(window.location.href)}
                        marketPlace={sellerMarketplace ?? undefined}
                        message={
                            <>
                                {stringFormat(
                                    T._(
                                        `Your current plan allows for {0} published products, and you've reached your limit! To publish this product, upgrade your plan to increase your published product slots.`,
                                        'Admin'
                                    ),
                                    numMaxPublishedProducts.toString()
                                )}
                            </>
                        }
                        onUpgradeCompleted={async () => performSave(productStatus, true, destination)}
                    />
                );

                setSaving(false);
                return;
            }
        }
        const loadingNotification = showNotification({
            type: 'default',
            message: T._('The product is saving...please wait and do not exit the page.', 'Admin')
        });

        try {
            // A standalone product stays unpublished while a new publication destination is chosen in Test Mode.
            // Saving an already published mini-site product must preserve its current status.
            const status =
                isStandaloneProduct && productStatus === ProductStatus.Published ? ProductStatus.UnPublished : (productStatus ?? state.product.statusID);

            // Save product
            let mutationInputForUpdateProduct = toUpdateMutationInput(status, state.salesChannelID, state.product, state.sides);

            console.log({
                mutationInputForUpdateProduct
            });

            let compressedMutationInput = compressToBase64(JSON.stringify(mutationInputForUpdateProduct));

            const response = await new Promise<previewUpdateMutation$data>((resolve, reject) =>
                commitUpdate({
                    onCompleted: (response) => {
                        invalidate();
                        resolve(response);
                    },
                    onError: (response) => reject(response),
                    updater: (store) => store.invalidateStore(),
                    variables: config.enableCompressionForSavingReadingProducts
                        ? {
                              compressedInput: compressedMutationInput
                          }
                        : {
                              input: mutationInputForUpdateProduct
                          }
                })
            );

            let modelSaved = response.createOrUpdateModelV2.model!;

            if (state.product.colorMappings.length > 0 && state.product.additionalCodes.length > 0) {
                let colorMappingsForSavedModel = state.product.colorMappings.filter(
                    (m) => modelSaved.colors.some((c) => c.colorID == m.colorID) || m.productColor
                );

                // Save color mapping for sales channel
                await new Promise<previewSetModelColorMappingSalesChannelsMutation$data>((resolve, reject) =>
                    updateModelColorMappingsSalesChannels({
                        variables: {
                            modelID: state.product.id!,
                            modelColorMappings: colorMappingsForSavedModel.map((m) => {
                                return {
                                    colorID:
                                        m.productColor != undefined && m.productColor
                                            ? (modelSaved?.colors.find((c) => c.code === m.productColor!.code)?.colorID ?? -1)
                                            : m.colorID,
                                    salesChannelID: m.salesChannelID,
                                    merchantCode: m.merchantCode
                                } as ModelColorMappingInput;
                            })
                        },
                        onError: (error) => {
                            removeNotification(loadingNotification);
                            showNotification({
                                type: 'danger',
                                message: <>{T._('An error occurred while mapping product variants. Please try again.', 'Admin')}</>,
                                dismiss: {
                                    duration: 3000
                                }
                            });
                            reject(error);
                        },
                        onCompleted: (response) => {
                            resolve(response);
                        }
                    })
                );
            }

            if (!state.product.id) {
                dispatch(loadProductAction(response!.createOrUpdateModelV2?.model!));
            }

            removeNotification(loadingNotification);
            showNotification({
                type: 'success',
                message: T._('Product successfully saved!', 'Admin'),
                dismiss: {
                    duration: 3000
                }
            });

            dispatch(setAreasWereDeletedAction(false));

            if (productStatus === ProductStatus.Published && status === ProductStatus.UnPublished) {
                setSaving(false);

                const params = new URLSearchParams();
                params.set('product_id', modelSaved.modelID?.toString() ?? '');
                params.set(
                    'publish',
                    destination === 'CONVERT' && isStandaloneConversionEnabled
                        ? 'convert'
                        : destination === 'STORE' || destination === 'CONVERT'
                          ? 'store'
                          : 'true'
                );

                // Clear unsaved changes tracking before navigating
                unsavedChanges.resetSnapshot();
                navigate(backofficeUrl(`/product/done?${params.toString()}`));
            } else {
                // Clear unsaved changes tracking before navigating
                unsavedChanges.resetSnapshot();
                changeStep('done');
                setSaving(false);
            }
        } catch (ex) {
            console.error(ex);
            showNotification({
                type: 'danger',
                message: T._('Failed saving product. Try again.', 'Admin'),
                dismiss: {
                    duration: 3000
                }
            });
            setSaving(false);
        }

        removeNotification(loadingNotification);
    };

    const hasVariants = !isDefaultColor(state.product.colors[0]);

    const [expandedVariants, setExpandedVariants] = useState<Set<number>>(new Set());

    const toggleVariant = (index: number) => {
        setExpandedVariants((prev) => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    };

    // The review card matches the attributes-management section (width & height) for every product.
    const ReviewCard = ReviewSection;

    // Size the Product Structure list to fill down to the fixed footer and scroll internally, so the
    // review card itself never scrolls.
    const detailsTreeRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
        const adjust = () => {
            const el = detailsTreeRef.current;
            if (!el) return;
            const top = el.getBoundingClientRect().top;
            const footerAndGap = 72; // thin fixed footer (~55px) + breathing room
            const available = window.innerHeight - top - footerAndGap;
            el.style.maxHeight = `${Math.max(140, available)}px`;
        };
        adjust();
        window.addEventListener('resize', adjust);
        return () => window.removeEventListener('resize', adjust);
    }, [state.product.name, state.product.code]);

    return (
        <>
            <ContainerBase>
                <ReviewCard>
                    {(isAllowed(['PRODUCTS_EDIT']) && <SaveTitle>{T._('Almost ready to publish your product and start selling.', 'Admin')}</SaveTitle>) || (
                        <SaveTitle>{T._('You are seeing the product as read-only', 'Admin')}</SaveTitle>
                    )}
                    <PreviewCardContentWrapper>
                        <ProductImageContainer>{state.product.imageUrl ? <ProductImage src={state.product.imageUrl} /> : <NoImage />}</ProductImageContainer>
                        <ProductDetailsContainer>
                            <ProductInfoRow>
                                <span>{T._('Name:', 'Admin')}</span> {state.product.name}
                                <span style={{ marginLeft: '16px' }}>{T._('Code:', 'Admin')}</span> {state.product.code}
                            </ProductInfoRow>

                            <div>
                                <DetailsSectionTitle>{T._('Product Structure', 'Admin')}</DetailsSectionTitle>
                                <DetailsTree $fill ref={detailsTreeRef}>
                                    {!hasVariants
                                        ? state.product.colors[0].sides.map((modelSide, sIdx) => {
                                              const side = state.sides.find((x) => x.guid == modelSide.originalSideGuid);
                                              const ppcm = modelSide.ppcm;
                                              return (
                                                  <React.Fragment key={sIdx}>
                                                      <TreeSide>
                                                          <TreeIcon>&#9724;</TreeIcon>
                                                          <span>{side?.name || `${T._('Side', 'Admin')} ${sIdx + 1}`}</span>
                                                      </TreeSide>
                                                      {Object.keys(modelSide.areas).map((areaIndex) => {
                                                          const area = modelSide.areas[parseInt(areaIndex)];
                                                          const dimLabel =
                                                              ppcm && area.boundsWidth && area.boundsHeight
                                                                  ? `${(area.boundsWidth / ppcm).toFixed(1)} × ${(area.boundsHeight / ppcm).toFixed(1)} cm`
                                                                  : null;
                                                          return (
                                                              <TreePrintArea key={areaIndex}>
                                                                  <TreeIcon>&#9679;</TreeIcon>
                                                                  <span>{area.name || `${T._('Print Area', 'Admin')} ${parseInt(areaIndex) + 1}`}</span>
                                                                  {dimLabel && <TreePrintAreaDim>{dimLabel}</TreePrintAreaDim>}
                                                              </TreePrintArea>
                                                          );
                                                      })}
                                                  </React.Fragment>
                                              );
                                          })
                                        : state.product.colors.map((modelColor, cIdx) => {
                                              const isExpanded = expandedVariants.has(cIdx);
                                              const sides = modelColor.sides;
                                              return (
                                                  <TreeVariant key={cIdx}>
                                                      <TreeVariantHeader $clickable={sides.length > 0} onClick={() => toggleVariant(cIdx)}>
                                                          <TreeIcon>{sides.length > 0 ? (isExpanded ? '▾' : '▸') : '–'}</TreeIcon>
                                                          <span>{modelColor.name}</span>
                                                          <TreeItemMeta>
                                                              {sides.length} {sides.length === 1 ? T._('side', 'Admin') : T._('sides', 'Admin')}
                                                          </TreeItemMeta>
                                                      </TreeVariantHeader>
                                                      {isExpanded &&
                                                          sides.map((modelSide, sIdx) => {
                                                              const side = state.sides.find((x) => x.guid == modelSide.originalSideGuid);
                                                              const ppcm = modelSide.ppcm;
                                                              return (
                                                                  <React.Fragment key={sIdx}>
                                                                      <TreeSide>
                                                                          <TreeIcon>&#9724;</TreeIcon>
                                                                          <span>{side?.name || `${T._('Side', 'Admin')} ${sIdx + 1}`}</span>
                                                                      </TreeSide>
                                                                      {Object.keys(modelSide.areas).map((areaIndex) => {
                                                                          const area = modelSide.areas[parseInt(areaIndex)];
                                                                          const dimLabel =
                                                                              ppcm && area.boundsWidth && area.boundsHeight
                                                                                  ? `${(area.boundsWidth / ppcm).toFixed(1)} × ${(area.boundsHeight / ppcm).toFixed(1)} cm`
                                                                                  : null;
                                                                          return (
                                                                              <TreePrintArea key={areaIndex}>
                                                                                  <TreeIcon>&#9679;</TreeIcon>
                                                                                  <span>
                                                                                      {area.name || `${T._('Print Area', 'Admin')} ${parseInt(areaIndex) + 1}`}
                                                                                  </span>
                                                                                  {dimLabel && <TreePrintAreaDim>{dimLabel}</TreePrintAreaDim>}
                                                                              </TreePrintArea>
                                                                          );
                                                                      })}
                                                                  </React.Fragment>
                                                              );
                                                          })}
                                                  </TreeVariant>
                                              );
                                          })}
                                </DetailsTree>
                            </div>
                        </ProductDetailsContainer>
                    </PreviewCardContentWrapper>
                </ReviewCard>
            </ContainerBase>
            <PageFooterButtons
                $fixed
                fullWidth
                leftContainerContent={isAllowed(['PRODUCTS_EDIT']) && <WizardFooterBackButton onClick={() => window.history.back()} />}
            >
                {(isAllowed(['PRODUCTS_EDIT']) && (
                    <>
                        {state.product.statusID === ProductStatus.UnPublished && !state.product.isDemo && (
                            <BasicButton
                                $size='large'
                                disabled={isSavingProduct || saving || isUpdateMappingSalesChannelsInProgress}
                                onClick={() => handleSaveClick(ProductStatus.Published, isStandaloneProduct && hasOnlineStore ? 'STORE' : undefined)}
                            >
                                {T._('Save and publish', 'Admin')}
                            </BasicButton>
                        )}
                        {state.product.statusID === ProductStatus.UnPublished &&
                            !state.product.isDemo &&
                            isStandaloneProduct &&
                            hasOnlineStore &&
                            isStandaloneConversionEnabled && (
                                <BasicButton
                                    $size='large'
                                    disabled={isSavingProduct || saving || isUpdateMappingSalesChannelsInProgress}
                                    onClick={() => handleSaveClick(ProductStatus.Published, 'CONVERT')}
                                >
                                    {T._('Save and convert to a store product', 'Admin')}
                                </BasicButton>
                            )}
                        {/*{state.product.statusID === ProductStatus.Published && (*/}
                        {/*	<BasicButton*/}
                        {/*		$size="large"*/}
                        {/*		disabled={isSavingProduct || saving || isUpdateMappingSalesChannelsInProgress}*/}
                        {/*		onClick={() => handleSaveClick(ProductStatus.UnPublished)}*/}
                        {/*	>*/}
                        {/*		{T._('Save and unpublish', 'Admin')}*/}
                        {/*	</BasicButton>*/}
                        {/*)}*/}
                        <PrimaryButton
                            $size='large'
                            disabled={isSavingProduct || saving || isUpdateMappingSalesChannelsInProgress}
                            onClick={() => handleSaveClick()}
                        >
                            {T._('Save', 'Admin')}
                        </PrimaryButton>
                    </>
                )) || (
                    <PrimaryButton $size='large' onClick={() => navigate(backofficeUrl('/products'))}>
                        {T._('Return to the product list', 'Admin')}
                    </PrimaryButton>
                )}
            </PageFooterButtons>
        </>
    );
};

export default Preview;
